import React, { useState } from "react";
import { Spinner, Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";

const STATUS_COLOR = {
  open: { bg: "#eef2ff", color: "#6366f1" },
  in_progress: { bg: "#fff7ed", color: "#f59e0b" },
  resolved: { bg: "#f0fdf4", color: "#10b981" },
  closed: { bg: "#f3f4f6", color: "#9ca3af" },
};

const formatRelative = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const SupportTicketList = ({ tickets, selectedId, onSelect, onNewTicket, loading, unreadPerTicket = {} }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    setSubmitting(true);
    try {
      await onNewTicket(subject.trim(), body.trim());
      setShowModal(false);
      setSubject("");
      setBody("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 16px",
        borderBottom: "1px solid #f3f4f6",
      }}>
        <span style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>
          {t("support.page_title")}
        </span>
        {onNewTicket && (
          <button
            onClick={() => setShowModal(true)}
            style={{
              display: "inline-flex", alignItems: "center", gap: "5px",
              padding: "5px 12px", borderRadius: "8px",
              background: "#6366f1", color: "#fff",
              border: "none", fontSize: "12px", fontWeight: 600,
              cursor: "pointer", transition: "all 0.15s",
            }}
          >
            + {t("support.new_ticket")}
          </button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spinner size="sm" style={{ color: "#6366f1" }} />
        </div>
      ) : tickets.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "#9ca3af" }}>
          <div style={{ fontSize: "28px", marginBottom: "8px", opacity: 0.4 }}>🎧</div>
          <div style={{ fontSize: "13px" }}>{t("support.no_tickets")}</div>
        </div>
      ) : (
        <div>
          {tickets.map((ticket) => {
            const active = ticket.ticket_id === selectedId;
            const statusStyle = STATUS_COLOR[ticket.status] || { bg: "#f3f4f6", color: "#9ca3af" };
            const unread = unreadPerTicket[ticket.ticket_id] ?? 0;
            return (
              <button
                key={ticket.ticket_id}
                onClick={() => onSelect(ticket.ticket_id)}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "12px 16px",
                  borderBottom: "1px solid #f3f4f6",
                  background: active ? "#f5f3ff" : "#fff",
                  borderLeft: active ? "3px solid #6366f1" : "3px solid transparent",
                  border: "none",
                  borderBottomWidth: "1px",
                  borderBottomStyle: "solid",
                  borderBottomColor: "#f3f4f6",
                  borderLeftWidth: "3px",
                  borderLeftStyle: "solid",
                  borderLeftColor: active ? "#6366f1" : "transparent",
                  cursor: "pointer", transition: "all 0.15s",
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = "#fafafa"; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = "#fff"; }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "55%" }}>
                    {ticket.subject}
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
                    {unread > 0 && (
                      <span style={{
                        background: "#ef4444", color: "#fff",
                        borderRadius: "10px", fontSize: "10px", fontWeight: 700,
                        padding: "1px 6px", lineHeight: "16px",
                      }}>
                        {unread}
                      </span>
                    )}
                    <span style={{
                      background: statusStyle.bg, color: statusStyle.color,
                      borderRadius: "6px", fontSize: "10px", fontWeight: 700,
                      padding: "2px 7px", textTransform: "uppercase", letterSpacing: "0.04em",
                    }}>
                      {t(`support.status_${ticket.status}`) || ticket.status}
                    </span>
                  </div>
                </div>
                {(ticket.user_email || ticket.user_firstname) && (
                  <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "3px" }}>
                    {ticket.user_firstname
                      ? `${ticket.user_firstname} ${ticket.user_lastname || ""}`.trim()
                      : ticket.user_email}
                  </div>
                )}
                <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>
                  {formatRelative(ticket.updated_at)}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* New Ticket Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: "1px solid #f3f4f6" }}>
          <Modal.Title style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>
            {t("support.new_ticket")}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body style={{ padding: "20px 24px" }}>
            <div style={{ marginBottom: "14px" }}>
              <div style={{ fontSize: "10px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "5px" }}>
                {t("support.subject_label")}
              </div>
              <Form.Control
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t("support.subject_placeholder")}
                required
                maxLength={255}
                style={{ borderRadius: "8px", fontSize: "13px", border: "1px solid #e5e7eb" }}
              />
            </div>
            <div>
              <div style={{ fontSize: "10px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "5px" }}>
                {t("support.message_placeholder")}
              </div>
              <Form.Control
                as="textarea"
                rows={4}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={t("support.message_placeholder")}
                required
                style={{ borderRadius: "8px", fontSize: "13px", border: "1px solid #e5e7eb", resize: "none" }}
              />
            </div>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: "1px solid #f3f4f6", gap: "8px" }}>
            <button
              type="button"
              onClick={() => setShowModal(false)}
              style={{ padding: "8px 16px", borderRadius: "8px", border: "1px solid #e5e7eb", background: "#f3f4f6", color: "#374151", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}
            >
              {t("pricing.cancel")}
            </button>
            <button
              type="submit"
              disabled={submitting || !subject.trim() || !body.trim()}
              style={{
                padding: "8px 16px", borderRadius: "8px", border: "none",
                background: (submitting || !subject.trim() || !body.trim()) ? "#e5e7eb" : "#6366f1",
                color: (submitting || !subject.trim() || !body.trim()) ? "#9ca3af" : "#fff",
                fontSize: "13px", fontWeight: 600,
                cursor: (submitting || !subject.trim() || !body.trim()) ? "not-allowed" : "pointer",
                display: "inline-flex", alignItems: "center", gap: "6px",
              }}
            >
              {submitting ? <Spinner size="sm" /> : t("support.submit_ticket")}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default SupportTicketList;
